import {
	TCurrency,
	TCurrencyCode,
	TCurrencyPairsObject,
} from "@sembark-travel/money"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"
import { useAuthUser } from "../Auth"
import { IUser } from "../Users"
import { useMemo } from "react"

export type TCurrencyExchangeRate = {
	id: number
	base_currency: string
	counter_currency: string
	rate: number
	rate_markup_percentage?: number | null
	/** The current global (market) rate for this pair, if synced. */
	global_rate?: TCurrencyExchangeRate | null
	/** Rate actually applied after markup; null when it can't be resolved. */
	effective_rate?: string | null
	created_at: string
	created_by?: IUser
}

export function currenciesXHR(xhr: XHRInstance) {
	return {
		async getAllAvailableCurrencies(params?: unknown) {
			return xhr
				.get<{
					data: Array<{
						id: string
						name: string
						iso: string
						locale: string
						rounding: string | null
					}>
				}>(`/currencies/all-available`, { params })
				.then((resp) => resp.data)
		},
		async getPendingPaymentCurrencies(params?: Record<string, unknown>) {
			return xhr
				.get<{
					data: Array<string>
				}>(`/payments/get-pending-payment-currencies`, { params })
				.then((resp) => resp.data)
		},
	}
}

/**
 * Convert a numeric value to a plain (non-scientific) decimal string, trimming
 * trailing zeros. Leaves blank/non-numeric values untouched.
 */
export function toPlainDecimalString(value: unknown): unknown {
	if (value === "" || value === null || value === undefined) return value
	const n = Number(value)
	if (!Number.isFinite(n)) return value
	const str = String(n)
	if (!/e/i.test(str)) return str
	return n.toFixed(20).replace(/\.?0+$/, "")
}

export function currenciesExchangeRatesXHR(xhr: XHRInstance) {
	return {
		async get(params?: unknown) {
			return xhr
				.get<IListResponse<TCurrencyExchangeRate>>(`/currency-exchange-rates`, {
					params,
				})
				.then((resp) => resp.data)
		},
		async store(payload: unknown) {
			// Expand a scientific-notation rate (e.g. 1e-7 from a number input)
			// to a plain decimal so it is stored/displayed in full.
			const data = (payload ?? {}) as Record<string, unknown>
			const body = { ...data, rate: toPlainDecimalString(data.rate) }
			return xhr
				.post<{
					data: TCurrencyExchangeRate
				}>(`/currency-exchange-rates`, body)
				.then((resp) => resp.data)
		},
		async destroy(id: number) {
			return xhr
				.delete<{ data: { message: string } }>(`/currency-exchange-rates/${id}`)
				.then((resp) => resp.data)
		},
		async getRatesForPairs(pairs: Array<[string, string]>) {
			return xhr
				.get<{ data: TCurrencyPairsObject }>(
					`/currency-exchange-rates-for-pairs`,
					{
						params: { pairs },
					}
				)
				.then((resp) => resp.data.data)
		},
	}
}

export function useCurrenciesOfTenant(): Array<TCurrency> {
	const { user } = useAuthUser()
	return (
		user?.tenant?.all_currencies || [
			{
				currency: user?.tenant?.functional_currency || "INR",
				rounding: null,
				format: "en_IN",
			},
		]
	)
}

export function useFunctionalCurrencyOfTenant(fallback = "INR"): TCurrency {
	const { user } = useAuthUser()
	return {
		currency: user?.tenant?.functional_currency || fallback,
		rounding: null,
		format: "en_IN",
	}
}

export function useAllAvailableCurrencies() {
	const xhr = useXHR()
	return useSWR(
		`/api/currencies/all-available`,
		() =>
			currenciesXHR(xhr)
				.getAllAvailableCurrencies()
				.then((resp) => resp.data),
		{ revalidateOnFocus: false }
	)
}

export function useCurrenciesCodesOfTenant(): Array<TCurrencyCode> {
	const currencies = useCurrenciesOfTenant()
	return useMemo(() => currencies.map((c) => c.currency), [currencies])
}
